/* eslint-disable react/no-children-prop */
import { useQuery } from "@apollo/client";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { generate } from "shortid";
import {
  Image,
  ContentBlockData,
  getSlug,
  GET_MEMA_PAGE,
  GET_PAGE,
  PageTitle,
  RelatedPage,
  showLoad,
  PanuiGridTypeData,
} from "@plinknz/tah-website-elements";
import { Hero } from "../components/hero";
import { Menu } from "../components/menu";
import * as ROUTES from "../config/router";
import { removeLeadingSlash } from "../utility/remove-leading-slash";
import { NotFound } from "./404";
import { BlockQuote } from "../components/content-blocks/blockquote";
import { CustomerFormBlock } from "../components/content-blocks/forms/customer-form-block";
import { CallToActionBlock } from "../components/content-blocks/call-to-action-block";
import { Content } from "../components/content-blocks/content";
import { DownloadBlock } from "../components/content-blocks/download-block";
import { GalleryBlock } from "../components/content-blocks/gallery";
import { GoogleCalendar } from "../components/content-blocks/google-calendar";
import { LinksBlock } from "../components/content-blocks/links-block";
import { NewsGridBlock } from "../components/content-blocks/news-grid-block";
import { PanuiGridBlock } from "../components/content-blocks/panui-grid-block";
import { PictureList } from "../components/content-blocks/picture-list";
import { RarangiPakihi } from "../components/content-blocks/rarangi-pakihi";
import { Waiata } from "../components/content-blocks/waiata";
import { YoutubePlayerContentBlock } from "../components/content-blocks/youtube-player";
import { UserContext } from "../service/reducers/user";
import { Loader } from "../components/loader";

export const PAGE_SUFFIX = "Rangitāne";

export interface PageData {
  id: number;
  title: string;
  subtitle?: string;
  slug?: "title";
  cover_image?: Image[];
  thumbnail_image?: null;
  show_children_pages: boolean | null;
  children: RelatedPage[];
  parent: RelatedPage;
  content: ContentBlockData[];
}

export interface PageQueryData {
  pages: PageData[];
}

export interface MemberPageQueryData {
  memberOnlyPages: PageData[];
}

export interface PageProps {
  suffix?: string;
  slugOverride?: string;
  memberPages?: boolean;
}

export const Page = ({
  suffix = PAGE_SUFFIX,
  slugOverride,
  memberPages = false,
}: PageProps) => {
  const location = useLocation();
  const slug = slugOverride || getSlug(location);
  const variables = { filters: { slug: { eq: slug } } };
  const { dispatch } = React.useContext(UserContext);
  const history = useHistory();
  const calculatedSuffix =
    slug !== removeLeadingSlash(ROUTES.home()) ? suffix : "";

  const { loading, data, error } = memberPages
    ? useQuery<MemberPageQueryData>(GET_MEMA_PAGE, { variables })
    : useQuery<PageQueryData>(GET_PAGE, { variables });

  if (!data && showLoad(loading)) {
    return (
      <div className="splash">
        <Loader size="large" />
      </div>
    );
  }

  const pages = memberPages
    ? (data as MemberPageQueryData)?.memberOnlyPages
    : (data as PageQueryData)?.pages;

  if (!showLoad(loading) && !error && pages?.length === 0) {
    return <NotFound />;
  }

  if (error) {
    if (
      error.toString().match("Invalid token") ||
      error.toString().match("401")
    ) {
      console.warn("Resetting stale token");
      dispatch({ type: "reset" });
      history.push(ROUTES.home());
    }
    console.warn(error);

    return (
      <Hero
        title="There was an unexpected error"
        subtitle={error?.networkError?.message}
      />
    );
  }

  if (!pages || pages.length < 1) {
    return <NotFound />;
  }

  const page = pages[0];

  const showChildPages = page?.show_children_pages;

  return (
    <>
      <Hero
        title={page.title}
        subtitle={page.subtitle}
        images={page.cover_image}
        hasChildPages={showChildPages && page.children.length > 0}
      />
      <PageTitle title={page.title} suffix={calculatedSuffix} />
      <Menu
        links={page.children}
        parent={page.parent}
        currentPage={page}
        show={!!showChildPages}
      />
      <section>
        {page.content.map((block) => {
          const key = generate();
          // eslint-disable-next-line no-underscore-dangle
          switch (block.__typename) {
            case "ComponentContentBlocksQuote":
              return <BlockQuote key={key} data={block} />;
            case "ComponentContentBlocksCustomerForm":
              return <CustomerFormBlock key={key} data={block} />;
            case "ComponentContentBlocksCallToAction":
              return <CallToActionBlock key={key} data={block} />;
            case "ComponentContentBlocksContent":
              return <Content key={key} data={block} />;
            case "ComponentContentBlocksDownloadFiles":
              return <DownloadBlock key={key} data={block} />;
            case "ComponentContentBlocksImageGallery":
              return <GalleryBlock key={key} data={block} />;
            case "ComponentContentBlocksGoogleCalendar":
              return <GoogleCalendar key={key} data={block} />;
            case "ComponentContentBlocksLinks":
              return <LinksBlock key={key} data={block} />;
            case "ComponentMemberMemberPageGrid":
            case "ComponentContentBlocksPageGrid":
              return (
                <NewsGridBlock
                  key={key}
                  data={block}
                  children={page.children}
                />
              );
            case "ComponentContentBlocksPanuiGrid":
              return (
                <PanuiGridBlock
                  key={key}
                  data={(block as unknown) as PanuiGridTypeData}
                />
              );
            case "ComponentContentBlocksPictureList":
              return <PictureList key={key} data={block} />;
            case "ComponentContentBlocksWaiata":
              return <Waiata key={key} data={block} />;
            case "ComponentContentBlocksRarangiPakihi":
              return <RarangiPakihi key={key} data={block} />;

            case "ComponentContentBlocksYouTubePlayer":
              return <YoutubePlayerContentBlock key={key} data={block} />;
            default:
              return null;
          }
        })}
      </section>
    </>
  );
};
